<template>
    <div class="comments-section">
        <h3 class="fs-16 font-weight-bold clr-title mb-4" v-if="isLoading || comments.length > 0">Comments</h3>

        <div class="comment_listwrapper">
            <div :class="['comment_list', { scrollable: comments.length > 4 }]">

                <!-- SKELETON LOADER -->
                <template v-if="isLoading">
                    <div v-for="n in 2" :key="n" class="mb-4">
                        <div class="comment_item d-flex align-items-start gap-3">
                            <b-skeleton type="avatar" width="46px" height="46px"></b-skeleton>

                            <div class="comment_content d-flex flex-column w-100 gap-2">
                                <div class="d-flex align-items-center gap-2">
                                    <b-skeleton width="120px" height="16px"></b-skeleton>
                                    <b-skeleton width="80px" height="14px"></b-skeleton>
                                </div>

                                <b-skeleton width="95%" height="14px"></b-skeleton>
                                <b-skeleton width="80%" height="14px"></b-skeleton>
                            </div>
                        </div>

                        <div class="mt-3">
                            <div class="d-flex align-items-center gap-3 document-skeleton">
                                <b-skeleton type="avatar" width="34px" height="34px"></b-skeleton>

                                <div class="d-flex flex-column gap-2 w-100">
                                    <b-skeleton width="60%" height="14px"></b-skeleton>
                                    <b-skeleton width="40%" height="14px"></b-skeleton>
                                </div>
                            </div>
                        </div>
                        <hr class="my-3">
                    </div>
                </template>
                <!-- REAL COMMENTS -->
                <template v-else>
                    <div v-for="(comment, index) in comments" :key="index">
                        <div class="comment_item d-flex align-items-start gap-3">
                            <div class="avatar_user flex-shrink-0">
                                <img src="@/assets/images/userimg.svg" width="46" height="46" class="rounded-circle" />
                            </div>

                            <div class="comment_content d-flex flex-column gap-1">
                                <div class="comment_header d-flex align-items-center gap-1 flex-wrap">
                                    <span class="comment_author fs-16 clr-black fw-semibold mr-2">
                                        {{ comment.author ? comment.author : "Author" }}
                                    </span>
                                    <span class="comment_date clr-bold-grey font-13">{{ comment.created_at }}</span>
                                </div>

                                <p class="comment_text fs-16 mb-0 clr-title" v-html="comment.comment" />
                            </div>
                        </div>

                        <b-card v-if="comment.documents != null" class="accordion-section document-section">
                            <Carousel :perPage="3" :scrollPerPage="true" :navigationEnabled="false" :paginationEnabled="true" :perPageCustom="[[0, 1], [768, 2], [1024, 3]]">
                                <Slide v-for="document in comment.documents" :key="document.id">
                                    <div class="ticket_documentInfo d-flex align-items-center gap-3 h-100">
                                        <div class="icon-wrapper flex-shrink-0">
                                            <img :src="getFileIcon(document.ext)" :alt="`${document.ext} icon`" class="w-100 h-100"/>
                                        </div>

                                        <div class="document_content d-flex flex-column gap-1 flex-grow-1">
                                            <a 
                                                :href="document.path" 
                                                class="document_link d-flex align-items-center gap-1 justify-content-between"
                                                target="_blank"
                                                :title="`Click to view ${document.original_name}. Use browser's 'Save As' to download.`"
                                            >
                                                <span class="document_name clr-primary fs-16 fw-semibold">{{ document.original_name }}</span>
                                                <span class="link_icon d-flex align-items-center justify-content-center clr-primary font-14">
                                                    <i class="fas fa-external-link-alt"></i>
                                                </span>
                                            </a>

                                            <div class="document_info d-flex align-items-center gap-2 flex-wrap font-14">
                                                <span class="document_type clr-bold-grey">{{ document.ext }}</span>
                                                <span class="dots"> </span>
                                                <span class="document_datewrapper clr-title fw-medium">
                                                    Uploaded :
                                                    <span class="document_date clr-bold-grey fw-normal">
                                                        {{ document.created_at }}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Slide>
                            </Carousel>
                        </b-card>

                        <hr class="mb-2">
                    </div>
                </template>

            </div>
        </div>

        <!-- Add Comment Box -->
        <div class="mt-4 mb-4" v-if="status=='Open'">
            <validation-observer ref="observer">
                <input-quill-editor
                    :vmodel.sync="form.comment"
                    :rules="commentRules"
                    placeholder="Add your message here ..."
                    formGroupClass="editorInputWrapper"
                    label="Comment"
                />
            </validation-observer>
        </div>

        <div class="button-wrapper d-flex justify-content-end align-items-center gap-3 flex-wrap" v-if="status=='Open'">
            <label for="fileInput" class="btn attach-btn d-flex align-items-center">
                <i class="fas fa-paperclip mr-2"></i> Attach File
            </label>
            <span class="file-name">
                {{ form.comment_document.length ? form.comment_document.map(f => f.name).join(', ') : 'No file chosen' }}
            </span>
            <input type="file" id="fileInput" @change="handleFileChange" hidden multiple>

            <b-button class="univ-btn" type="submit" @click="submit">Comment</b-button>
        </div>

    </div>
</template>

<script>
import InputQuillEditor from "@/components/form/InputQuillEditor";
import Api from "@/resource/Api";
import { Carousel, Slide } from 'vue-carousel';

export default {
    name: "TicketIssueComment",
    components: {
        InputQuillEditor,
        Carousel,
        Slide,
    },
    props: {
        ticketStatus: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            comments: "",
            newComment: "",
            status: this.ticketStatus,
            form: {
                issue_id: "",
                comment: "",
                comment_document: [],
            },
            isLoading: false,
            iconMap: {
                pdf: require('@/assets/images/pdf.svg'),
                doc: require('@/assets/images/file-regular-full.svg'),
                docx: require('@/assets/images/file-regular-full.svg'),
                xls: require('@/assets/images/excel.svg'),
                xlsx: require('@/assets/images/excel.svg'),
                png: require('@/assets/images/image-regular-full.svg'),
                jpg: require('@/assets/images/image-regular-full.svg'),
                jpeg: require('@/assets/images/image-regular-full.svg'),
                gif: require('@/assets/images/image-regular-full.svg'),
                txt: require('@/assets/images/file-regular-full.svg'),
            }
        };
    },
    created() {
        this.isLoading = true;
        this.getComments();
    },
    methods: {
        getComments() {
            const ticketId = this.$route.params.ticketId;
            const url = `tickets/${ticketId}/comments`
            Api.get(url)
            .then((res) => {
                if(res.data.status==="success" && res.data?.data) {
                    this.comments = res.data.data;
                }
            })
            .catch(err => console.error("API Error:", err))
            .finally(() => {
                this.isLoading = false;
            });
        },
        handleFileChange(event) {
            const files = Array.from(event.target.files); // convert FileList to Array
            this.form.comment_document = files;
        },
        async submit() {
            const isValid = await this.$refs.observer.validate();
            if (!isValid) {
                return
            }

            let loader = this.$loading.show();
            let formData = new FormData();

            formData.append('issue_id', this.$route.params.ticketId);
            formData.append('comment', this.form.comment);
            // Append files
            if (this.form.comment_document.length) {
                this.form.comment_document.forEach((file, index) => {
                    formData.append(`comment_document[${index}]`, file);
                });
            }
            Api.post('tickets/comments', formData)
                .then((res) => {
                    if(res.data.status) {
                        this.isLoading = true;
                        this.form = {
                            issue_id: "",
                            comment: "",
                            comment_document: [],
                        };
                        this.$notify(res.data.message, "Success");
                        this.$nextTick(() => {
                            this.$refs.observer.reset()
                        })
                        this.getComments();
                    }
                })
                .catch((err) => {
                    this.$notify(err.response?.data?.message || 'An error occurred', "Error");
                })
                .finally(() => {
                    loader.hide();
                });
        },
        getFileIcon(extension) {
            return this.iconMap[extension?.toLowerCase()] || this.iconMap.doc;
        },
    },
    computed: {
        commentRules() {
            return { required: true }
        }
    }
};
</script>

<style scoped>
.ticket-manage-wrapper .scrollable {
    max-height: 320px;
    overflow-y: auto;
    padding-right: 8px;
}

.ticket-manage-wrapper .scrollable::-webkit-scrollbar {
    width: 6px;
}

.ticket-manage-wrapper .scrollable::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
}

.ticket-manage-wrapper .univ-btn,
.ticket-manage-wrapper .attach-btn {
     padding: 10px 20px;
    width: fit-content;
    font-size: 14px;
    margin: 0;
    border-radius: 6px;
}

.ticket-manage-wrapper .attach-btn , .ticket-manage-wrapper .attach-btn:hover , .ticket-manage-wrapper .attach-btn:focus {
    background-color: rgba(3, 195, 236, 10%);
    color: var(--rep-primary-clr);
    border: 1px solid var(--rep-primary-clr);
    box-shadow: none;
    outline: none;
}

.document-section .ticket_documentInfo {
    padding: 16px;
    border: 1px solid #d7e1ea;
    border-radius: 6px;
    margin-right: 16px;
}

.document-section .ticket_documentInfo .icon-wrapper {
    width: 34px;
}

.icon-wrapper {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
